import React from 'react';
import { ProjectMember, Invitation } from '../../../types/domain';
import { EriciusTable } from '../../../components/ui/EriciusTable';
import { Button } from '../../../components/ui/Button';
import {
  Settings,
  Trash2,
  Copy,
} from 'lucide-react';

interface OrganizationTeamTabProps {
  members: ProjectMember[];
  isLoadingMembers: boolean;
  canManageOrg: boolean;
  user: any;
  invitations: Invitation[];
  onManageProjects: (m: ProjectMember) => void;
  onSelectMemberToRemove: (m: ProjectMember) => void;
  onRevokeInvitation: (invId: string) => void;
  isRevokingInvite: string | null;
  showToast: (opts: any) => void;
}

export const OrganizationTeamTab: React.FC<OrganizationTeamTabProps> = ({
  members,
  isLoadingMembers,
  canManageOrg,
  user,
  invitations,
  onManageProjects,
  onSelectMemberToRemove,
  onRevokeInvitation,
  isRevokingInvite,
  showToast,
}) => {
  return (
    <div className="space-y-6">
      <EriciusTable<ProjectMember>
        data={members}
        isLoading={isLoadingMembers}
        columns={[
          {
            key: 'member',
            label: 'MEMBER',
            render: (m) => {
              const isCurrentUser =
                user &&
                (m.user_id === user.id ||
                  m.id === user.id ||
                  (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()));
              return (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-cf-orange/20 text-cf-orange flex items-center justify-center font-bold text-xs shrink-0">
                    {(m.full_name || m.email || user?.email || 'U')[0].toUpperCase()}
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-cf-textPrimary truncate">
                      {m.full_name || (isCurrentUser ? user?.full_name : null) || 'Member'}
                    </span>
                    {isCurrentUser && (
                      <span className="px-1.5 py-0.5 rounded bg-cf-orange/20 border border-cf-orange/40 text-cf-orange text-xs font-semibold shrink-0">
                        YOU
                      </span>
                    )}
                  </div>
                </div>
              );
            },
          },
          {
            key: 'email',
            label: 'EMAIL',
            render: (m) => {
              const isCurrentUser =
                user &&
                (m.user_id === user.id ||
                  m.id === user.id ||
                  (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()));
              return (
                <span className="text-cf-textSecondary">
                  {m.email || (isCurrentUser ? user?.email : 'N/A')}
                </span>
              );
            },
          },
          {
            key: 'role',
            label: 'ROLE',
            render: (m) => (
              <span className="px-1.5 py-0.5 rounded bg-cf-surface border border-cf-border text-cf-orange uppercase text-xs font-semibold">
                {m.role || m.permission_tier || 'member'}
              </span>
            ),
          },
          {
            key: 'status',
            label: 'STATUS',
            render: () => (
              <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">
                Active
              </span>
            ),
          },
          ...(canManageOrg
            ? [
                {
                  key: 'actions' as const,
                  label: 'Actions',
                  align: 'right' as const,
                  render: (m: ProjectMember) => {
                    const isCurrentUser =
                      user &&
                      (m.user_id === user.id ||
                        m.id === user.id ||
                        (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()));
                    const isOwner = (m.role || '').toLowerCase() === 'owner';
                    return (
                      <div className="inline-flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => onManageProjects(m)}
                          title="Member settings & project access"
                        >
                          <Settings size={14} />
                        </Button>
                        {!isCurrentUser && !isOwner && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            onClick={() => onSelectMemberToRemove(m)}
                            title="Remove from organization"
                            className="text-cf-textSecondary hover:text-rose-400"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    );
                  },
                },
              ]
            : []),
        ]}
        emptyMessage="No team members loaded."
      />

      {canManageOrg && invitations.length > 0 && (
        <div className="space-y-3 pt-4 border-t border-cf-border/60">
          <div>
            <h3 className="text-sm font-bold text-cf-textPrimary">
              Pending Invitations ({invitations.length})
            </h3>
            <p className="text-xs text-cf-textSecondary mt-0.5">
              Invited users who have not yet accepted their invitation link.
            </p>
          </div>

          <div className="overflow-x-auto border border-cf-border rounded-lg bg-cf-surface">
            <table className="w-full text-left text-xs">
              <thead className="bg-cf-tableHeader dark:bg-[#2e323c] text-cf-textSecondary border-b border-cf-border dark:border-[#2e323c]">
                <tr>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Role</th>
                  <th className="px-3 py-2.5">Expires</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cf-border/60">
                {invitations.map((inv) => {
                  const inviteLink = `${window.location.origin}/invite/${inv.token || inv.id}`;
                  return (
                    <tr key={inv.id} className="bg-cf-surface hover:bg-cf-surfaceHover transition-colors">
                      <td className="px-3 py-2.5 font-medium text-cf-textPrimary">
                        {inv.email}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-orange uppercase text-xs font-semibold">
                          {inv.role || inv.permission_tier || 'member'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-cf-textMuted">
                        {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '7 days'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="xs"
                            onClick={() => {
                              navigator.clipboard.writeText(inviteLink);
                              showToast({
                                type: 'success',
                                title: 'Link Copied',
                                message: `Invite link for ${inv.email} copied to clipboard.`,
                              });
                            }}
                            icon={<Copy size={12} />}
                            title="Copy signed invite link"
                          >
                            Copy Link
                          </Button>
                          {canManageOrg && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              disabled={isRevokingInvite === inv.id}
                              loading={isRevokingInvite === inv.id}
                              onClick={() => onRevokeInvitation(inv.id)}
                              className="text-cf-textSecondary hover:text-rose-400"
                              title="Revoke invitation"
                            >
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
